import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { courseAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Loader2, 
  FileText,
  BookOpen,
  Maximize2,
  Minimize2,
  Pencil,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { HtmlContentWithPdfSlides } from "@/components/editor/PdfSlidesRenderer";

// Script to inject in iframe srcdoc to handle anchor links properly
const iframeNavScript = `<script data-nav-handler="true">
  document.addEventListener('click', function(e) {
    var link = e.target.closest('a');
    if (!link) return;
    var href = link.getAttribute('href');
    if (!href) return;
    
    if (href.startsWith('#')) {
      e.preventDefault();
      e.stopPropagation();
      var targetId = href.slice(1);
      var target = document.getElementById(targetId) || document.querySelector('[name="' + targetId + '"]');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return false;
    }
    
    if (href.startsWith('http://') || href.startsWith('https://')) {
      e.preventDefault();
      window.open(href, '_blank', 'noopener,noreferrer');
      return false;
    }
  }, true);
<\/script>`;

// Script to auto-resize iframe to its content height
const iframeResizeScript = `<script data-resize-handler="true">
  function reportHeight() {
    var h = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    if (h > 0) {
      window.parent.postMessage({ type: 'iframe-resize', height: h + 30 }, '*');
    }
  }
  window.addEventListener('load', function() { setTimeout(reportHeight, 200); });
  new MutationObserver(reportHeight).observe(document.body, { childList: true, subtree: true, attributes: true });
  setTimeout(reportHeight, 500);
  setTimeout(reportHeight, 1500);
<\/script>`;

// Helper function to clean HTML content - remove pointer-events-none from Google Slides containers
// and inject script to handle anchor links in iframes to avoid breaking the parent app routing
const cleanHtmlContent = (content) => {
  if (!content) return '';
  let cleaned = content
    .replace(/pointer-events-none\s*/g, '')
    .replace(/select-none\s*/g, '');
    
  try {
    if (cleaned.includes('iframe')) {
      const parser = new DOMParser();
      
      const processIframes = (documentNode) => {
        let isModified = false;
        const iframes = documentNode.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          // Add fullscreen permissions for embedded content
          if (!iframe.hasAttribute('allowfullscreen')) {
            iframe.setAttribute('allowfullscreen', 'true');
            iframe.setAttribute('webkitallowfullscreen', 'true');
            iframe.setAttribute('mozallowfullscreen', 'true');
            isModified = true;
          }
          // Also set the allow attribute for modern browsers
          let allowAttr = iframe.getAttribute('allow') || '';
          if (!allowAttr.includes('fullscreen')) {
            iframe.setAttribute('allow', allowAttr ? `${allowAttr}; fullscreen` : 'fullscreen');
            isModified = true;
          }

          let srcdoc = iframe.getAttribute('srcdoc');
          if (srcdoc) {
            // Process the inner srcdoc HTML recursively
            let innerDoc = parser.parseFromString(srcdoc, 'text/html');
            let innerModified = processIframes(innerDoc);
            
            if (innerModified) {
              srcdoc = innerDoc.body.innerHTML;
              isModified = true;
            }
            
            if (!srcdoc.includes('data-nav-handler')) {
              const scriptsToInject = iframeNavScript + iframeResizeScript;
              if (srcdoc.includes('</body>')) {
                srcdoc = srcdoc.replace('</body>', scriptsToInject + '</body>');
              } else if (srcdoc.includes('</html>')) {
                srcdoc = srcdoc.replace('</html>', scriptsToInject + '</html>');
              } else {
                srcdoc = srcdoc + scriptsToInject;
              }
              isModified = true;
            }
            
            if (isModified) {
              iframe.setAttribute('srcdoc', srcdoc);
            }
          }
        });
        return isModified;
      };

      const doc = parser.parseFromString(cleaned, 'text/html');
      if (processIframes(doc)) {
        cleaned = doc.body.innerHTML;
      }
    }
  } catch (e) {
    console.error('Error cleaning iframe content:', e);
  }
  
  return cleaned;
};

export default function SectionViewPage() {
  const { workspaceId, courseId, sectionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const canManage = user?.role === "admin" || user?.role === "trainer";

  useEffect(() => {
    loadSection();
  }, [courseId, sectionId]);

  // Listen for iframe auto-resize messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'iframe-resize' && event.data.height) {
        // Find the iframe that sent this message
        const iframes = document.querySelectorAll('.section-content iframe[srcdoc]');
        iframes.forEach(iframe => {
          if (iframe.contentWindow === event.source) {
            iframe.style.height = event.data.height + 'px';
            iframe.style.minHeight = event.data.height + 'px';
          }
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [section]);

  const loadSection = async () => {
    try {
      const response = await courseAPI.get(workspaceId, courseId);
      const courseData = response.data;
      setCourse(courseData);
      
      // Find the section
      const foundSection = courseData.sections?.find(s => s.id === sectionId);
      if (foundSection) {
        setSection(foundSection);
      } else {
        toast.error("Section introuvable");
        navigate(`/workspace/${workspaceId}/courses`);
      }
    } catch (error) {
      console.error("Failed to load section:", error);
      toast.error("Erreur lors du chargement de la section");
      navigate(`/workspace/${workspaceId}/courses`);
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  // Get current section index and navigation
  const getSectionNavigation = () => {
    if (!course?.sections) return { prevSection: null, nextSection: null, currentIndex: 0, total: 0 };
    
    const sortedSections = [...course.sections].sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIndex = sortedSections.findIndex(s => s.id === sectionId);
    
    return {
      prevSection: currentIndex > 0 ? sortedSections[currentIndex - 1] : null,
      nextSection: currentIndex < sortedSections.length - 1 ? sortedSections[currentIndex + 1] : null,
      currentIndex: currentIndex + 1,
      total: sortedSections.length
    };
  };

  const { prevSection, nextSection, currentIndex, total } = getSectionNavigation();

  const navigateToSection = (sec) => {
    if (sec) {
      navigate(`/workspace/${workspaceId}/courses/${courseId}/sections/${sec.id}`);
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  if (!section || !course) {
    return null;
  }

  return (
    <WorkspaceLayout>
      <div className={`animate-fade-in ${isFullscreen ? 'fixed inset-0 z-50 bg-background p-6 overflow-auto' : 'page-container'}`}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/courses`)}
            data-testid="back-to-courses-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20 shrink-0">
            <FileText className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="section-title">{section.title}</h1>
              <Badge variant="outline" className="text-muted-foreground">
                Section {currentIndex}/{total}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <BookOpen className="h-4 w-4" />
                {course.title}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={toggleFullscreen} data-testid="fullscreen-btn">
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            {canManage && (
              <Button 
                variant="outline" 
                onClick={() => navigate(`/workspace/${workspaceId}/courses/${courseId}/edit`)}
                data-testid="edit-course-btn"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Modifier le cours
              </Button>
            )}
          </div>
        </div>

        {/* Description */}
        {section.description && (
          <p className="text-muted-foreground mb-6 text-lg">{section.description}</p>
        )}

        {/* Content */}
        <div className="border rounded-lg bg-card">
          <style>{`
            .section-content [data-google-slides] iframe {
              min-height: 500px !important;
            }
            .section-content [data-drive-video] iframe {
              min-height: 450px !important;
              height: 450px !important;
            }
            .section-content [data-html-block] iframe,
            .section-content div.my-4 > iframe[srcdoc] {
              width: 100% !important;
              border: none !important;
            }
            ${user?.role !== 'admin' ? `
            .section-content [data-google-slides] .aspect-video::after {
              content: '';
              position: absolute;
              bottom: 0;
              right: 0;
              width: 140px;
              height: 38px;
              background-color: #f1f3f4;
              pointer-events: all;
              z-index: 10;
            }
            .dark .section-content [data-google-slides] .aspect-video::after {
              background-color: #2b2b2b;
            }
            ` : ''}
          `}</style>
          <HtmlContentWithPdfSlides 
            html={cleanHtmlContent(section.content) || "<p class='text-muted-foreground'>Aucun contenu disponible pour cette section.</p>"}
            className="section-content prose prose-lg dark:prose-invert max-w-none p-8"
          />
        </div>

        {/* Navigation entre sections */}
        {total > 1 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <Button
              variant="outline"
              onClick={() => navigateToSection(prevSection)}
              disabled={!prevSection}
              className="gap-2"
              data-testid="prev-section-btn"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Section précédente</span>
              <span className="sm:hidden">Précédent</span>
            </Button>
            
            <span className="text-sm text-muted-foreground">
              {currentIndex} / {total}
            </span>
            
            <Button
              variant="outline"
              onClick={() => navigateToSection(nextSection)}
              disabled={!nextSection}
              className="gap-2"
              data-testid="next-section-btn"
            >
              <span className="hidden sm:inline">Section suivante</span>
              <span className="sm:hidden">Suivant</span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </WorkspaceLayout>
  );
}
